import React, { useState } from "react";
import { Layout, Menu, Breadcrumb } from "antd";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Content, Sider } = Layout;

const graphs = [
  {
    id: "graph1",
    title: "Evolución en el tiempo",
    menuTitle: "Evolución en el tiempo",
    text: `La proporción de niñas y niños en hogares con hacinamiento mostró un descenso sistemático entre 2006 y 2020, pasando de una proporción del 27% a niveles significativamente menores. Sin embargo, desde 
          2020 esta tendencia a la baja se interrumpió: la proporción dejó de disminuir y se estancó en 1 de cada 10 niñas y niños.`,
    link: "https://public.flourish.studio/visualisation/20952126/embed",
  },
  {
    id: "graph2",
    title: "Análisis por sexo",
    menuTitle: "\u2022 Sexo",
    text: `El hacinamiento puede tener repercusiones distintas según el género. Estudios han documentado que en los niños el hacinamiento se relaciona con mayores probabilidades de tener conductas impulsivas y 
          falta de control emocional (conductas externalizantes) y en las niñas con mayores probabilidades de sufrir depresión.7 Los datos indican que niñas y niños presentan la misma proporción de hacinamiento 
          en todos los años de medición.`,
    link: "https://public.flourish.studio/visualisation/20952143/embed",
  },
  {
    id: "graph3",
    title: "Análisis por edad",
    menuTitle: "\u2022 Edad",
    text: `En cuanto a la etapa del ciclo vital, se observa que existe una mayor proporción de niñas y niños que viven hacinados en la primera infancia. En 2024, un 16% de las niñas y los niños entre 0 y 5 años 
          vive en hogares con hacinamiento. En la infancia media (6 a 12 años), un 12% vive en hogares con hacinamiento, y entre las y los adolescentes un 8%. Se observa que el porcentaje de niñas y niños viviendo 
          en hacinamiento disminuyó entre 2006-2020 para todos los tramos etarios, sin embargo, desde esa medición se observa un estancamiento en todas las edades.`,
    link: "https://public.flourish.studio/visualisation/20952150/embed",
  },
  {
    id: "graph4",
    title: "Análisis por país de nacimiento",
    menuTitle: "\u2022 País de nacimiento",
    text: `Para niñas y niños nacidos en el país, el hacinamiento mostró una tendencia constante a la baja entre 2006 y el 2020, pero esta situación se ha mantenido estancada desde entonces. Por otro lado, entre 
          las niñas y los niños nacidos en otros países se observa una trayectoria con fluctuaciones no estadísticamente significativas pero que no ha disminuido. Actualmente, la proporción de niñas y niños nacidos 
          en otros países que viven en hogares con hacinamiento es tres veces más alta que lo que se observa entre las niñas y los niños que nacieron en el país.`,
    link: "https://public.flourish.studio/visualisation/20952161/embed",
  },
  {
    id: "graph5",
    title: "Análisis por pueblo originario",
    menuTitle: "\u2022 Pueblo originario",
    text: `Históricamente, ha existido una brecha entre niñas y niños que se identifican con pueblos originarios y aquellos que no lo hacen en la proporción de niñas y niños en hogares hacinados. En 2006, el porcentaje 
          de hacinamiento de las niñas y los niños pertenecientes a pueblos originarios era 12% mayor. Esta brecha se cerró hacia el año 2022, ya que hubo una baja más pronunciada de los porcentajes de hacinamiento entre 
          las niñas y los niños pertenecientes a pueblos originarios, situación que se mantiene al 2024.`,
    link: "https://public.flourish.studio/visualisation/20952177/embed",
  },
  {
    id: "graph6",
    title: "Análisis por quintil de ingresos",
    menuTitle: "\u2022 Quintil de ingresos",
    text: `A lo largo de toda la serie histórica se mantiene una tendencia estructural: la probabilidad de habitar en un hogar con hacinamiento está estrechamente condicionada por el nivel de ingresos. Si bien el 
          comportamiento es transversal a todos los quintiles —con una marcada tendencia a la baja entre 2006 y 2020 seguida de un estancamiento—, los niveles de hacinamiento varían considerablemente según el nivel 
          socioeconómico. En particular, la última medición de 2024 subraya esta brecha: la proporción de niñas y niños en situación de hacinamiento en el primer quintil (19%) es casi 14 veces superior a la registrada 
          en el quintil de mayores ingresos (1%).`,
    link: "https://public.flourish.studio/visualisation/28121459/embed",
  },
  {
    id: "graph7",
    title: "Análisis por zona",
    menuTitle: "\u2022 Zona",
    text: `En la actualidad, la proporción de niñas y niños que viven hacinados es la misma en las zonas urbanas y rurales y la tendencia a la baja se replica en ambos grupos. Únicamente en 2006 y 2020 hubo diferencias 
          significativas entre las niñas y los niños de zonas urbanas y rurales. En 2006, era levemente mayor la proporción de niñas y niños hacinados de zonas rurales y en 2020 fue mayor en el caso de niñas y niños de 
          zonas urbanas.`,
    link: "https://public.flourish.studio/visualisation/20952171/embed",
  },
  {
    id: "graph8",
    title: "Análisis por región",
    menuTitle: "\u2022 Región",
    text: `En todas las regiones se observa una importante disminución en la proporción de niñas y niños que viven en hogares hacinados entre 2006 y 2015. A partir de ese año, la tendencia a la baja pierde intensidad, 
          dando paso a un periodo de estancamiento desde 2020 que se mantiene hasta la medición de 2024. En ella, la región que tiene una mayor proporción con un 16% es Tarapacá, seguida por Arica y Parinacota, con un 
          15%. Por otro lado, Coquimbo y La Araucanía son las regiones que tienen una menor proporción de niñas y niños en hogares hacinados, ambas con un 8%.`,
    link: "https://public.flourish.studio/visualisation/20952208/embed",
  }
];

const App = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("graph1");

  const handleMenuItemClick = (graphId, graphTitle) => {
    setSelectedMenuItem(graphId);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const selectedGraph = graphs.find((graph) => graph.id === selectedMenuItem);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={250}
        collapsible
        collapsed={collapsed}
        onCollapse={toggleCollapsed}
        style={{ background: "#fff" }}
        breakpoint="lg"
        collapsedWidth={0}
      >
        <Menu theme="dark" mode="vertical" selectedKeys={[selectedMenuItem]} style={{ background: "#fff" }}>
          {graphs.map((graph) => (
            <Menu.Item
              key={graph.id}
              onClick={() => handleMenuItemClick(graph.id)}
              style={{ color: 'black', paddingLeft: graph.id !== "graph1" ? "30px" : "20px" }}
            >
              {graph.menuTitle}
            </Menu.Item>
          ))}
        </Menu>
      </Sider>
      <Layout style={{ padding: "24px 24px 24px" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item></Breadcrumb.Item>
        </Breadcrumb>
        <Content
          id="content"
          style={{
            background: "#ffff",
            padding: 24,
            margin: 0,
            minHeight: 280,
          }}
        >
          {selectedGraph && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <h1>{selectedGraph.title}</h1>
              </div>
              <div style={{ textAlign: 'justify' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {selectedGraph.text}
                </ReactMarkdown>
              </div>
              <iframe
                src={selectedGraph.link}
                frameBorder="0"
                scrolling="no"
                style={{ minWidth: "100%", height: "500px" }}
              ></iframe>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;


